import { useEffect } from 'react'

/**
 * Keeping a phone on the newest version of the app; registerServiceWorker (which can't run under test) wires
 * these up.
 * - The app asks for a new version when it opens, each time it comes back on screen and every half hour. Phones
 *   keep the app in the background for days: on 21 October 2026 the owner's phone ran the old sign-in hours
 *   after a deploy and waited for a code the server no longer sends.
 * - A downloaded new version is used at once while a screen that loses nothing shows (the first sign-in screen).
 *   Anywhere else the toast asks first: a share-out typed into the calculator, or a half-filled form, lives only
 *   in memory.
 */

/** Besides each time the app comes back on screen, how often an open app asks for a new version. */
export const CHECK_EVERY_MS = 30 * 60 * 1000

let switchNow: (() => void) | null = null
let safeScreens = 0

/** Ask for a new version each time the app comes back on screen, and every half hour. */
export function keepCheckingForNewVersions(registration: ServiceWorkerRegistration): void {
  const check = () => {
    if (registration.installing || !navigator.onLine) return
    registration.update().catch(() => {}) // offline or the server unreachable: the next check tries again
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check()
  })
  setInterval(check, CHECK_EVERY_MS)
}

/** A new version has downloaded. True if it is being used at once, because a screen that loses nothing shows. */
export function newVersionWaiting(switchToIt: () => void): boolean {
  switchNow = switchToIt
  if (safeScreens === 0) return false
  switchToIt()
  return true
}

/** While `safe` (a reload would lose nothing), a waiting new version is used at once. */
export function useNewVersionAtOnce(safe: boolean): void {
  useEffect(() => {
    if (!safe) return
    safeScreens += 1
    switchNow?.()
    return () => {
      safeScreens -= 1
    }
  }, [safe])
}
